import { useMemo } from 'react'

import { useRouter } from 'next/router'

import { CategoryPath } from '@config/categories'

import { getAllowedQueryParametersFromQuery } from '@utils/getAllowedQueryParametersFromQuery'
import {
    SupportedQueryParameters,
    getAppStateFromQueryParameters,
} from '@utils/getAppStateFromQueryParameters'
import { getPathnameFromAsPath } from '@utils/getPathnameFromAsPath'
import { getQueryParametersFromAppState } from '@utils/getQueryParametersFromAppState'

import useRouterParams from './useRouterParams'

type AllowedRoutes =
    | 'alternative-products'
    | CategoryPath
    | ['all-loans', CategoryPath | null]
    | ''

const useAppQueryState = () => {
    /**
     * This fixes an issue with NextJS unable to
     * get router params if there is a trailing
     * slash in the url.
     */
    const routerParams = useRouterParams()

    const router = useRouter()

    const appQueryState = useMemo(() => {
        return getAppStateFromQueryParameters({
            ...router.query,
            ...routerParams,
        })
    }, [router.query, routerParams])

    const getRouteWithAppState = (route: AllowedRoutes) => {
        const queryParameters = {
            ...getQueryParametersFromAppState(appQueryState),
            ...getAllowedQueryParametersFromQuery(router.query),
        }

        const routePath =
            typeof route === 'string'
                ? route === ''
                    ? '/'
                    : `/${route}/`
                : `/${route.join('/')}/`

        return {
            pathname: routePath,
            query: queryParameters,
        }
    }

    const getQueryStringWithAppState = () => {
        const queryParameters = {
            ...getQueryParametersFromAppState(appQueryState),
            ...getAllowedQueryParametersFromQuery(router.query),
        }

        return Object.keys(queryParameters)
            .filter(parameterKey => (queryParameters as any)[parameterKey])
            .map(
                parameterKey =>
                    `${parameterKey}=${(queryParameters as any)[parameterKey]}`
            )
            .join('&')
    }

    const getRouteUrl = (route: AllowedRoutes) => {
        const routeWithAppState = getRouteWithAppState(route)
        return `${process.env.NEXT_PUBLIC_BASE_PATH}${
            routeWithAppState.pathname
        }?${getQueryStringWithAppState()}`
    }

    const getRoutePath = (route: AllowedRoutes) => {
        const routeWithAppState = getRouteWithAppState(route)
        return `${process.env.NEXT_PUBLIC_BASE_PATH}${routeWithAppState.pathname}`
    }

    const goToRouteWithAppState = (
        route: AllowedRoutes,
        options?: {
            hash?: string
            queryParameters?: SupportedQueryParameters
            shallow?: boolean
        }
    ) => {
        const routeWithAppState = getRouteWithAppState(route)

        router.push(
            {
                pathname: routeWithAppState.pathname,
                query: {
                    ...routeWithAppState.query,
                    ...options?.queryParameters,
                },
                hash: options?.hash,
            },
            undefined,
            options
        )
    }

    const updateAppQueryState = (
        queryParameters: SupportedQueryParameters,
        options?: { hash?: string; shallow?: boolean }
    ) => {
        router.replace(
            {
                pathname: getPathnameFromAsPath(router.asPath),
                query: {
                    ...getQueryParametersFromAppState(appQueryState),
                    ...queryParameters,
                    ...getAllowedQueryParametersFromQuery(router.query),
                },
                hash: options?.hash,
            },
            undefined,
            { shallow: options?.shallow ?? true }
        )
    }

    return {
        isReady: router.isReady,
        appQueryState,
        goToRouteWithAppState,
        getRouteWithAppState,
        updateAppQueryState,

        getRouteUrl,
        getRoutePath,
        getQueryStringWithAppState,
    }
}

export default useAppQueryState
